import './TicketForm.scss';

import { InfoCircleOutlined } from '@ant-design/icons';
import { useResourceParams } from '@refinedev/core';
import { Button, DatePicker, Flex, Form, FormProps, Input, message, Radio } from 'antd';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import CompanyContactInfoModal from '@/components/general/CompanyContactInfoModal';
import FormattedTextInput from '@/components/general/FormattedTextInput';
import InfoTooltip from '@/components/general/InfoTooltip';
import LinkTextField from '@/components/general/LinkTextField';
import PrioritySelect from '@/components/general/PrioritySelect';
import UploadedFileCarousel from '@/components/general/UploadFile/UploadedFileCarousel';
import UploadFileComponent from '@/components/general/UploadFile/UploadFile';
import { MACHINES_URLS } from '@/constants/urls';
import FileData from '@/model/General/FileData';
import { TICKET_STATE, TicketStateString, TicketTypeString } from '@/model/Ticket/TicketConstants';
import { RESOURCES } from '@/Resources';
import { normalizeHtmlWhitespace } from '@/utils/ensureHtml';

import { useTicketForm } from './useTicketForm';

interface TicketFormProps {
  formProps: FormProps;
  fileList: FileData[];
  setFileList: (files: FileData[]) => void;
  setBlockLogisticTab?: (block: boolean) => void;
}

const TicketForm = (props: TicketFormProps) => {
  const { t } = useTranslation();
  const { action } = useResourceParams();
  const { formProps, fileList, setFileList, setBlockLogisticTab } = props;
  const edit = useMemo(() => action === 'edit', [action]);
  const {
    companySelectProps,
    subCategorySelectProps,
    usersSelectProps,
    subStatusSelectProps,
    machineSelectProps,
    refTicketsSelectProps,
    formInitialValues,
    clearDraft,
  } = useTicketForm(formProps, edit, setBlockLogisticTab);
  const machine = Form.useWatch('movil', formProps.form);
  const company = Form.useWatch('empresa', formProps.form);
  const reference = Form.useWatch('referencia', formProps.form);

  return (
    <Form
      {...formProps}
      initialValues={formInitialValues}
      layout="vertical"
      className="TicketForm"
      onValuesChange={(changedValues, allValues) => {
        // Preserva el hook interno de refine para el warnWhenUnsavedChanges
        formProps.onValuesChange?.(changedValues, allValues);

        if (!formProps.form?.isFieldsTouched()) {
          return;
        }

        setBlockLogisticTab?.(true);
      }}
      onFinishFailed={errorInfo => {
        if (errorInfo.errorFields.length > 0) {
          message.error(t('Por favor, revisa los campos requeridos antes de enviar el formulario'));
        }
      }}
      onFinish={values => {
        // Normaliza espacios no separables innecesarios antes de persistir el HTML del editor.
        const normalizedDescription = normalizeHtmlWhitespace(values.description);
        const wrappedDescription = normalizedDescription
          ? `<div id="description">${normalizedDescription}</div>`
          : undefined;

        // Wrapper a description
        formProps.onFinish?.({
          ...values,
          description: wrappedDescription,
        });
        clearDraft?.();
      }}>
      <div className="TicketFormItemsContainer">
        <Form.Item
          label={t('Subcategoría')}
          name={'subcategoria'}
          className="TicketListFilterRow"
          rules={[
            {
              required: true,
              message: t('La subcategoría es requerida'),
            },
          ]}>
          <AntdSelect {...subCategorySelectProps} allowClear showSearch />
        </Form.Item>

        <Form.Item
          label={
            <>
              {t('Título')}
              <InfoTooltip
                tooltipText={t('Se autocompletara con el nombre de la subcategoría')}
                iconClassName="TicketTitleInfoIcon"
              />
            </>
          }
          name={'title'}>
          <Input />
        </Form.Item>
      </div>
      <div className="TicketFormItemsContainer">
        <Form.Item label={t('Empresa')}>
          <Flex align="center" gap={4}>
            <Form.Item name={'empresa'} noStyle>
              {/* <CompanySelector showFullName {...companySelectProps} allowClear showSearch /> */}
              <AntdSelect {...companySelectProps} allowClear showSearch />
            </Form.Item>
            {company && (
              <CompanyContactInfoModal
                company={company}
                openButton={<Button icon={<InfoCircleOutlined />} />}
              />
            )}
          </Flex>
        </Form.Item>
        <Form.Item label={t('Móvil')} name={'movil'}>
          <AntdSelect {...machineSelectProps} allowClear showSearch />
        </Form.Item>
      </div>
      <Form.Item label={t('Referencia')}>
        <Flex vertical gap={4}>
          <Form.Item noStyle name={'referencia'}>
            <AntdSelect {...refTicketsSelectProps} allowClear showSearch />
          </Form.Item>
          {reference && (
            <LinkTextField
              label={t('Ver ticket de referencia')}
              to={`/${RESOURCES.TICKETS}/edit/${reference}`}
              local
              newTab
            />
          )}
        </Flex>
      </Form.Item>
      <div className="TicketFormItemsContainer">
        <Form.Item
          label={t('Asignado a')}
          name={'assigned_to'}
          className="TicketListFilterRow"
          rules={[
            {
              required: true,
              message: t('Se requiere asignar un responsable'),
            },
          ]}>
          <AntdSelect options={usersSelectProps.options} allowClear showSearch />
        </Form.Item>
        <Form.Item
          label={t('Estado')}
          name={'status'}
          rules={[
            {
              required: true,
              message: t('El {{field}} es requerido', { field: t('Estado') }),
            },
            {
              validator: (_, value) => {
                if (!value || !edit) {
                  return Promise.resolve();
                }

                if (value != TICKET_STATE.CLOSED && value != TICKET_STATE.RESOLVED) {
                  return Promise.resolve();
                }

                const canClose = formInitialValues?.logistics_can_close;

                if (canClose) {
                  return Promise.resolve();
                } else {
                  return Promise.reject(
                    new Error(t('No puede finalizarse el ticket con logística pendiente')),
                  );
                }
              },
            },
          ]}>
          <AntdSelect
            options={Object.keys(TicketStateString).map(v => ({
              label: TicketStateString[parseInt(v)],
              value: parseInt(v),
            }))}
            allowClear
          />
        </Form.Item>
      </div>
      <Form.Item label={t('Subestado')} name={'substatus'} className="TicketListFilterRow">
        <AntdSelect {...subStatusSelectProps} allowClear showSearch />
      </Form.Item>
      <div className="TicketFormItemsContainer">
        <Form.Item label={t('Prioridad')} name={'priority'} className="TicketListFilterRow">
          <PrioritySelect />
        </Form.Item>
      </div>
      <div className="TicketFormItemsContainer">
        <Form.Item
          label={t('Tipo')}
          name={'contact_type'}
          rules={[
            {
              required: true,
              message: t('El {{field}} es requerido', { field: t('Tipo') }),
            },
          ]}>
          <Radio.Group
            className="TicketFormTypeRadioGroup"
            options={Object.values(TicketTypeString).map((v, index) => ({
              label: t(v),
              value: index + 1,
            }))}
          />
        </Form.Item>
        <Form.Item
          label={
            <>
              {t('Próximo recordatorio')}
              <InfoTooltip
                tooltipText={t('Se recibirá una notificación en la fecha seleccionada')}
              />
            </>
          }
          name={'reminder_date'}
          getValueProps={value => ({
            value: value ? dayjs(value) : undefined,
          })}>
          <DatePicker
            className="TicketFormDatePicker"
            format={'L'}
            placeholder={t('Seleccionar una fecha')}
          />
        </Form.Item>
      </div>
      {/* Links adicionales */}
      {edit && formInitialValues?.movil && (
        <div style={{ marginBottom: 16 }}>
          <div>{t('Links adicionales de máquina')}</div>
          <Flex className="TicketShowDataLinks" gap={24}>
            <LinkTextField
              label={t('Admin móvil')}
              to={`${MACHINES_URLS.ADMIN_MOVIL}/${machine}`}
              admin
            />
            <LinkTextField label={t('Info de móvil')} to={`/machines/detail/${machine}`} platform />
            <LinkTextField
              label={t('Ver historial de trabajos')}
              to={`/machines/works/${machine}`}
              platform
            />
            <LinkTextField
              label={t('Ver historial por hora')}
              to={`/machines/history/${machine}`}
              platform
            />
          </Flex>
        </div>
      )}
      <Form.Item label={t('Descripción')} name={'description'}>
        <FormattedTextInput readOnly={false} />
      </Form.Item>
      <Form.Item label={t('Archivos')}>
        <UploadFileComponent fileList={fileList} setFileList={setFileList} />
      </Form.Item>
      {fileList.length > 0 && (
        <Form.Item label={t('Archivos subidos')}>
          <UploadedFileCarousel fileList={fileList} setFileList={setFileList} />
        </Form.Item>
      )}
    </Form>
  );
};

export default TicketForm;
